import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Login = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get('redirect');
  const { login, register } = useAuth();
  const [loading, setLoading] = useState(false);
  const [legalVersions, setLegalVersions] = useState({ terms_version: '1.0', privacy_version: '1.0' });

  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [registerData, setRegisterData] = useState({ 
    email: '', 
    password: '', 
    full_name: '', 
    phone: '',
    terms_accepted: false
  });

  useEffect(() => {
    fetchLegalVersions();
  }, []);

  const fetchLegalVersions = async () => {
    try {
      const response = await axios.get(`${API}/legal/versions`);
      setLegalVersions(response.data);
    } catch (error) {
      console.error('Failed to fetch legal versions', error);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const userData = await login(loginData.email, loginData.password);
      // Check if user needs to re-accept legal terms
      if (userData.requires_legal_acceptance) {
        toast.info('Please review and accept our updated Terms & Conditions');
        // For now, just proceed - can implement modal for re-acceptance later
      }
      toast.success('Login successful');
      // Redirect to specified page or default to dashboard
      if (redirectTo) {
        navigate(`/${redirectTo}`);
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (!registerData.terms_accepted) {
      toast.error('You must accept the Terms & Conditions and Privacy Policy to register');
      return;
    }
    
    setLoading(true);
    try {
      await register(
        registerData.email, 
        registerData.password, 
        registerData.full_name, 
        registerData.phone,
        registerData.terms_accepted,
        legalVersions.terms_version,
        legalVersions.privacy_version
      );
      toast.success('Registration successful');
      // Redirect to specified page or default to dashboard
      if (redirectTo) {
        navigate(`/${redirectTo}`);
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background grain" data-testid="login-page">
      <div className="w-full max-w-md p-6">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-playfair font-bold text-primary tracking-tight" data-testid="login-title">Sharedequal</h1>
          <p className="text-sm text-muted-foreground mt-2">Buy as a group, pay less for shipping</p>
        </div>

        {/* Enticing Value Proposition */}
        <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 mb-6">
          <p className="text-sm text-center text-gray-700">
            <span className="font-semibold text-primary">Join our polo community!</span>
            <br />
            Create an account to join drops, request new brands, and stay updated on future opportunities. 
            Track your orders, view past purchases, and jump in whenever you're ready.
          </p>
        </div>

        <Tabs defaultValue="login" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="login" data-testid="login-tab">Login</TabsTrigger>
            <TabsTrigger value="register" data-testid="register-tab">Register</TabsTrigger>
          </TabsList>

          <TabsContent value="login">
            <Card className="border-border rounded-sm shadow-card" data-testid="login-form">
              <CardHeader>
                <CardTitle>Welcome Back</CardTitle>
                <CardDescription>Enter your credentials to access your account</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="login-email">Email</Label>
                    <Input
                      id="login-email"
                      data-testid="login-email-input"
                      type="email"
                      placeholder="polo@example.com"
                      value={loginData.email}
                      onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                      required
                      className="rounded-sm border-border focus:ring-1 focus:ring-primary"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="login-password">Password</Label>
                    <Input
                      id="login-password"
                      data-testid="login-password-input"
                      type="password"
                      value={loginData.password}
                      onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                      required
                      className="rounded-sm border-border focus:ring-1 focus:ring-primary"
                    />
                  </div>
                  <Button
                    type="submit"
                    data-testid="login-submit-button"
                    className="w-full bg-primary hover:bg-primary/90 text-primary-foreground rounded-sm uppercase tracking-wide text-sm shadow-subtle"
                    disabled={loading}
                  >
                    {loading ? 'Logging in...' : 'Login'}
                  </Button>
                  <div className="text-center mt-3">
                    <Link to="/forgot-password" className="text-sm text-muted-foreground hover:text-primary transition-colors">
                      Forgot password?
                    </Link>
                  </div>
                </form>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="register">
            <Card className="border-border rounded-sm shadow-card" data-testid="register-form">
              <CardHeader>
                <CardTitle>Create Account</CardTitle>
                <CardDescription>Join the group drop community</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleRegister} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="register-name">Full Name</Label>
                    <Input
                      id="register-name"
                      data-testid="register-name-input"
                      type="text"
                      placeholder="John Doe"
                      value={registerData.full_name}
                      onChange={(e) => setRegisterData({ ...registerData, full_name: e.target.value })}
                      required
                      className="rounded-sm border-border focus:ring-1 focus:ring-primary"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="register-email">Email</Label>
                    <Input
                      id="register-email"
                      data-testid="register-email-input"
                      type="email"
                      placeholder="polo@example.com"
                      value={registerData.email}
                      onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                      required
                      className="rounded-sm border-border focus:ring-1 focus:ring-primary"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="register-phone">Phone (Optional)</Label>
                    <Input
                      id="register-phone"
                      data-testid="register-phone-input"
                      type="tel"
                      placeholder="+65 1234 5678"
                      value={registerData.phone}
                      onChange={(e) => setRegisterData({ ...registerData, phone: e.target.value })}
                      className="rounded-sm border-border focus:ring-1 focus:ring-primary"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="register-password">Password</Label>
                    <Input
                      id="register-password"
                      data-testid="register-password-input"
                      type="password"
                      value={registerData.password}
                      onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                      required
                      className="rounded-sm border-border focus:ring-1 focus:ring-primary"
                    />
                  </div>
                  
                  {/* Legal Acceptance Checkbox */}
                  <div className="flex items-start space-x-3 pt-2">
                    <Checkbox
                      id="terms-accepted"
                      data-testid="terms-accepted-checkbox"
                      checked={registerData.terms_accepted}
                      onCheckedChange={(checked) => setRegisterData({ ...registerData, terms_accepted: checked })}
                      className="mt-1"
                    />
                    <label htmlFor="terms-accepted" className="text-sm text-muted-foreground leading-relaxed cursor-pointer">
                      I agree to the{' '}
                      <Link to="/terms-and-conditions" className="text-primary hover:underline" target="_blank">
                        Terms & Conditions
                      </Link>{' '}
                      and{' '}
                      <Link to="/privacy-policy" className="text-primary hover:underline" target="_blank">
                        Privacy Policy
                      </Link>
                    </label>
                  </div>
                  
                  <Button
                    type="submit"
                    data-testid="register-submit-button"
                    className="w-full bg-primary hover:bg-primary/90 text-primary-foreground rounded-sm uppercase tracking-wide text-sm shadow-subtle"
                    disabled={loading || !registerData.terms_accepted}
                  >
                    {loading ? 'Creating Account...' : 'Create Account'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        <div className="mt-6 text-center">
          <Link to="/" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
            ← Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Login;